/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Onboarding Contract — endpoints that move a user from
 *   landing-page pricing CTA → email-OTP sign-up → /pending-org →
 *   self-serve org-create OR admin-assigned org → calendar.
 *
 * Three endpoints:
 *   - `setIntent`            — user, persists chosen plan + billing +
 *                              waiting-flag onto User (server-side carrier
 *                              that survives both sessionStorage loss and
 *                              the redirect-URL being scrubbed).
 *   - `listWaitingUsers`     — admin, feeds the "Waiting Users" grid in
 *                              the admin dashboard.
 *   - `assignUserToNewOrg`   — admin, manual onboarding path.  Creates
 *                              an org with `subscriptionStatus = 'active'`
 *                              (offline / out-of-band cash payment, see
 *                              Q1 decision in the PR description) and adds
 *                              the waiting user as owner.
 *
 * Plan + billing intent flows from the URL (`/pending-org?plan=…&
 * billing=…`) into User on first land via `setIntent`, then into
 * Organization at create-time via the Better Auth
 * `beforeCreateOrganization` hook (in `auth-instance.ts`), then cleared
 * by the `afterCreateOrganization` hook.  The same intent fields seed
 * the admin assignment dialog.
 *
 * Auth/role enforcement happens inside the handler — `setIntent` requires
 * a valid session, the admin endpoints additionally require
 * `user.role === 'ADMIN'`.
 */

import { oc } from "@orpc/contract";
import * as z from "zod";
import { OrgPlanSchema } from "../common/org-plan";
import { commonErrors } from "./base";

// ---------------------------------------------------------------------------
// Common shapes
// ---------------------------------------------------------------------------

/**
 * Slug constraints — Discord-channel-ish: lowercase, digits, dashes only,
 * 1–50 chars, cannot start or end with a dash.  Consecutive dashes are
 * tolerated (e.g. "acme--co") because Better Auth's `checkSlug` is the
 * source of uniqueness truth and consecutive dashes are valid URL chars;
 * we don't add a stylistic rule that the platform itself doesn't enforce.
 */
export const OrgSlugSchema = z
	.string()
	.min(1, "Slug is required")
	.max(50, "Slug must be 50 characters or fewer")
	.regex(
		/^[a-z0-9-]+$/,
		"Lowercase letters, digits, and dashes only — no spaces, capitals, or special characters"
	)
	.refine(
		(s) => !(s.startsWith("-") || s.endsWith("-")),
		"Slug cannot start or end with a dash"
	);
export type OrgSlug = z.infer<typeof OrgSlugSchema>;

export const BillingIntervalSchema = z.enum(["monthly", "yearly"]);
export type BillingInterval = z.infer<typeof BillingIntervalSchema>;

// ---------------------------------------------------------------------------
// setIntent (user)
// ---------------------------------------------------------------------------

const SetIntentInputSchema = z.object({
	/**
	 * `true` when the user clicks "Wait for assignment" on /pending-org —
	 * surfaces them in the admin "Waiting Users" grid.  `false` reverts
	 * to the choice screen (orgless users may still create their own org
	 * after toggling this back off).
	 */
	waiting: z.boolean(),
	/**
	 * Plan the user picked on the landing-page pricing card.  Optional
	 * because the user may arrive at /pending-org by another path
	 * (direct sign-up, invitation acceptance fallback, …); when absent
	 * the org-create flow defaults to `chat`.
	 */
	plan: OrgPlanSchema.optional(),
	/**
	 * Billing cadence picked alongside the plan.  Optional for the same
	 * reasons as `plan`; defaults to `monthly` at org-create time.
	 */
	billing: BillingIntervalSchema.optional(),
});

const SetIntentOutputSchema = z.object({
	ok: z.literal(true),
	waitingForAssignment: z.boolean(),
	intendedPlan: OrgPlanSchema.nullable(),
	intendedBilling: BillingIntervalSchema.nullable(),
});

// ---------------------------------------------------------------------------
// listWaitingUsers (admin)
// ---------------------------------------------------------------------------

const WaitingUserRowSchema = z.object({
	id: z.string(),
	email: z.string().nullable(),
	name: z.string().nullable(),
	createdAt: z.string().datetime(),
	intendedPlan: OrgPlanSchema.nullable(),
	intendedBilling: BillingIntervalSchema.nullable(),
});
export type WaitingUserRow = z.infer<typeof WaitingUserRowSchema>;

const ListWaitingUsersOutputSchema = z.object({
	users: z.array(WaitingUserRowSchema),
});

// ---------------------------------------------------------------------------
// assignUserToNewOrg (admin)
// ---------------------------------------------------------------------------

const AssignUserToNewOrgInputSchema = z.object({
	userId: z.string().min(1),
	slug: OrgSlugSchema,
	plan: OrgPlanSchema,
	billing: BillingIntervalSchema,
	/**
	 * Optional human-readable org name.  When omitted, the slug is used
	 * verbatim.  Stored in `Organization.name` (separate from `slug` —
	 * Better Auth treats them independently).
	 */
	name: z.string().min(1).max(120).optional(),
});

const AssignUserToNewOrgOutputSchema = z.object({
	orgId: z.string(),
	slug: z.string(),
});

// ---------------------------------------------------------------------------
// assignUserToExistingOrg (admin)
// ---------------------------------------------------------------------------

/**
 * Per-org Member.role values. Mirrors the canonical set in
 * `server/src/application/superadmin/access/org-access-control.ts`.
 * "owner" is reserved for the user who created the org via self-serve;
 * super-user-driven assignment defaults to "secretary".
 */
const OrgMemberRoleSchema = z.enum(["owner", "admin", "secretary", "guest"]);

const AssignUserToExistingOrgInputSchema = z.object({
	userId: z.string().min(1),
	orgId: z.string().min(1),
	role: OrgMemberRoleSchema.default("secretary"),
});

const AssignUserToExistingOrgOutputSchema = z.object({
	orgId: z.string(),
	memberId: z.string(),
	role: OrgMemberRoleSchema,
});

const SetupStatusStepsSchema = z.object({
	businessProfile: z.boolean(),
	aiModel: z.boolean(),
	systemPrompt: z.boolean(),
	messaging: z.boolean(),
	schedule: z.boolean().optional(),
});

const SetupStatusOutputSchema = z.object({
	gated: z.boolean(),
	setupComplete: z.boolean(),
	steps: SetupStatusStepsSchema,
	requirementsMet: z.boolean(),
	plan: OrgPlanSchema,
});

const CompleteSetupOutputSchema = z.object({
	ok: z.literal(true),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const onboardingContract = {
	setIntent: oc
		.route({
			method: "POST",
			path: "/api/onboarding/intent",
			summary: "Persist the user’s plan / billing / wait-flag intent",
			description:
				"Idempotent.  Reads URL params on first land at /pending-org, " +
				'or the "Wait for assignment" toggle.  Cleared by the ' +
				"afterCreateOrganization hook on org-assignment.",
			tags: ["Onboarding"],
		})
		.input(SetIntentInputSchema)
		.output(SetIntentOutputSchema)
		.errors({ ...commonErrors }),

	listWaitingUsers: oc
		.route({
			method: "GET",
			path: "/api/admin/onboarding/waiting-users",
			summary: "Admin: list users waiting for org assignment",
			description:
				"Returns users with `waitingForAssignment = true`.  Drives the " +
				"Waiting Users grid in the admin dashboard.",
			tags: ["Onboarding", "Admin"],
		})
		.output(ListWaitingUsersOutputSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
		}),

	assignUserToNewOrg: oc
		.route({
			method: "POST",
			path: "/api/admin/onboarding/assign-user-to-new-org",
			summary: "Admin: create an org and assign a waiting user as owner",
			description:
				"Manual onboarding path. Creates Organization with the supplied " +
				'slug, plan, billing interval, and `subscriptionStatus = "active"` ' +
				"(orgs created via this path are paid offline). Adds the waiting " +
				"user as owner.  Clears the user’s onboarding-intent fields. " +
				"Returns the new org id + slug so the admin UI can confirm.",
			tags: ["Onboarding", "Admin"],
		})
		.input(AssignUserToNewOrgInputSchema)
		.output(AssignUserToNewOrgOutputSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
			CONFLICT: {
				message: "Slug already taken, or user is already a member of an org.",
				data: z.object({
					reason: z.enum(["slug_taken", "user_already_in_org"]),
				}),
			},
		}),

	assignUserToExistingOrg: oc
		.route({
			method: "POST",
			path: "/api/admin/onboarding/assign-user-to-existing-org",
			summary: "Admin: assign a waiting user to an existing org",
			description:
				"Super-user-only assignment path (2026-05-12 redesign). " +
				"Attaches the waiting user to an existing Organization as a " +
				"Member with the supplied role, clears `waitingForAssignment` " +
				"+ intent fields, syncs `User.orgId`, and writes a " +
				"UserOrgHistory.joinedAt row so the audit trail is complete " +
				"from day one. Distinct from `assignUserToNewOrg` which CREATES " +
				"a new org for the user — this endpoint joins an existing tenant.",
			tags: ["Onboarding", "Admin"],
		})
		.input(AssignUserToExistingOrgInputSchema)
		.output(AssignUserToExistingOrgOutputSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			FORBIDDEN: commonErrors.FORBIDDEN,
			NOT_FOUND: commonErrors.NOT_FOUND,
			CONFLICT: {
				message:
					"User is already a member of an org, or already in the target org.",
				data: z.object({
					reason: z.enum(["user_already_in_org", "already_member"]),
				}),
			},
		}),
};

export type OnboardingContract = typeof onboardingContract;

export type SetIntentInput = z.infer<typeof SetIntentInputSchema>;
export type SetIntentOutput = z.infer<typeof SetIntentOutputSchema>;
export type ListWaitingUsersOutput = z.infer<
	typeof ListWaitingUsersOutputSchema
>;
export type AssignUserToNewOrgInput = z.infer<
	typeof AssignUserToNewOrgInputSchema
>;
export type AssignUserToNewOrgOutput = z.infer<
	typeof AssignUserToNewOrgOutputSchema
>;
export type AssignUserToExistingOrgInput = z.infer<
	typeof AssignUserToExistingOrgInputSchema
>;
export type AssignUserToExistingOrgOutput = z.infer<
	typeof AssignUserToExistingOrgOutputSchema
>;
export type OrgMemberRole = z.infer<typeof OrgMemberRoleSchema>;
export type SetupStatusSteps = z.infer<typeof SetupStatusStepsSchema>;
export type SetupStatusOutput = z.infer<typeof SetupStatusOutputSchema>;
export type CompleteSetupOutput = z.infer<typeof CompleteSetupOutputSchema>;
